import type { RestaurantEvent } from '@shared/types/restaurants';
import { toEventUrlDateFormat } from './dateFormatters';
import { slugify } from './urlUtils';

type RestaurantSlugs = {
  locationSlug: string;
  nameSlug: string;
};

/**
 * The redesigned event ticket purchase page's URL identifies the event by the
 * slug stored when it was created, so renaming or re-dating the event keeps the
 * link working:
 * - events created with a name-only slug: `/palm-springs/normas/events/christmas-party`
 * - events that predate it keep their original name + date URL, which is part
 *   of their stored slug: `/palm-springs/normas/events/christmas-party/24-12-2026`
 *
 * Events without a stored slug fall back to deriving the legacy URL from the
 * current name and date.
 *
 * Shared so both `webReservations` (which links to this page) and
 * `restaurantAdmin` (which lets admins copy this URL) can build it.
 */
export const buildEventTicketPurchasePath = (
  restaurant: RestaurantSlugs,
  event: Pick<RestaurantEvent, 'name' | 'shift' | 'urlSlug'>,
) => {
  const eventsPath = `/${restaurant.locationSlug}/${restaurant.nameSlug}/events`;

  if (!event.urlSlug) {
    return `${eventsPath}/${slugify(event.name)}/${toEventUrlDateFormat(event.shift.startDate)}`;
  }
  return `${eventsPath}/${event.urlSlug}`;
};
